import React, { useRef, useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Container,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { signUpUser } from '../../../app/features/sessions/sessionSlice';
import { AppDispatch, RootState } from '../../../app/store';
import jinnerLogo from '../../../app/assets/logo-white-name-white-without-background-portrait.png';

const BG = '#15151a';
const CARD_BG = '#1c1c24';
const BRAND_RED = '#c0392b';
const TEXT = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.6)';
const BORDER = 'rgba(255,255,255,0.12)';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT,
        '& fieldset': { borderColor: BORDER },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px ${CARD_BG} inset`,
            WebkitTextFillColor: TEXT,
            caretColor: TEXT,
        },
    },
    '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f4f4f6', fontWeight: 700 },
    '& .MuiInputAdornment-root .MuiIconButton-root': { color: TEXT_SECONDARY },
};

function SignUp() {
    const nicknameRef = useRef<HTMLInputElement>();
    const emailRef = useRef<HTMLInputElement>();
    const passwordRef = useRef<HTMLInputElement>();
    const passwordConfirmationRef = useRef<HTMLInputElement>();
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const loading = useSelector((state: RootState) => state.session.loading);
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (
            !nicknameRef.current?.value ||
            !emailRef.current?.value ||
            !passwordRef.current?.value ||
            !passwordConfirmationRef.current?.value
        ) {
            setErrorMessages(['Please fill out all fields']);
            return;
        }
        const response = await dispatch(signUpUser({
            nickname: nicknameRef.current.value,
            email: emailRef.current.value,
            password: passwordRef.current.value,
            passwordConfirmation: passwordConfirmationRef.current.value,
        }));
        console.log('[SignUp][Component] dispatch concluído — requestStatus:', response.meta?.requestStatus, '| payload:', JSON.stringify(response.payload));
        if (response.meta?.requestStatus === 'rejected') {
            const p = response.payload as any;
            const fullMessages = p?.errors?.full_messages;
            const msgs: string[] = Array.isArray(fullMessages) && fullMessages.length > 0
                ? fullMessages
                : Array.isArray(p?.errors) && p.errors.length > 0
                    ? p.errors
                    : ['An error occurred. Please try again.'];
            console.warn('[SignUp][Component] registo rejeitado — msgs:', JSON.stringify(msgs));
            setSuccess(false);
            setErrorMessages(msgs);
        } else if ((response.payload as any)?.status === 'success') {
            console.log('[SignUp][Component] registo bem-sucedido');
            setErrorMessages([]);
            setSuccess(true);
        } else {
            console.warn('[SignUp][Component] estado inesperado — payload:', response.payload);
        }
    }

    console.log('[SignUp][Render] loading:', loading, '| success:', success, '| errorMessages:', JSON.stringify(errorMessages));

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG, display: 'flex', alignItems: 'center', py: 4 }}>
            <Container maxWidth="sm">
                <Card sx={{
                    bgcolor: CARD_BG,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                }}>
                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box component="img" src={jinnerLogo} alt="Jinner" sx={{ height: 80 }} />
                        </Box>

                        <Typography variant="h5" fontWeight={700} color={TEXT} gutterBottom>
                            Create your account
                        </Typography>

                        {loading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={32} sx={{ color: BRAND_RED }} />
                            </Box>
                        )}

                        {!loading && success ? (
                            <Alert severity="success" sx={{ mb: 2 }} aria-live="assertive">
                                Account created! Check your email to confirm your account.
                            </Alert>
                        ) : (
                            <Box>
                                {errorMessages.length > 0 && (
                                    <div style={{
                                        background: '#5f1a1a',
                                        border: '1px solid #c0392b',
                                        borderRadius: 6,
                                        color: '#f8d7d7',
                                        padding: '12px 16px',
                                        marginBottom: 16,
                                        fontSize: 14,
                                    }}>
                                        {errorMessages.map((e, i) => <div key={i}>{e}</div>)}
                                        <button
                                            onClick={() => setErrorMessages([])}
                                            style={{ float: 'right', background: 'none', border: 'none', color: '#f8d7d7', cursor: 'pointer', fontSize: 16, marginTop: -4 }}
                                        >✕</button>
                                    </div>
                                )}
                            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                                <TextField
                                    label="Nickname"
                                    type="text"
                                    inputRef={nicknameRef}
                                    fullWidth
                                    required
                                    variant="outlined"
                                    margin="normal"
                                    sx={fieldSx}
                                />
                                <TextField
                                    label="Email Address"
                                    type="email"
                                    inputRef={emailRef}
                                    fullWidth
                                    required
                                    variant="outlined"
                                    margin="normal"
                                    helperText={<span style={{ color: TEXT_SECONDARY }}>We&apos;ll never share your email.</span>}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    inputRef={passwordRef}
                                    fullWidth
                                    required
                                    variant="outlined"
                                    margin="normal"
                                    sx={fieldSx}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setShowPassword(s => !s)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label="Password Confirmation"
                                    type={showPassword ? 'text' : 'password'}
                                    inputRef={passwordConfirmationRef}
                                    fullWidth
                                    required
                                    variant="outlined"
                                    margin="normal"
                                    sx={fieldSx}
                                />

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={loading}
                                    sx={{
                                        mt: 3,
                                        py: 1.4,
                                        bgcolor: BRAND_RED,
                                        '&:hover': { bgcolor: '#a93226' },
                                        '&.Mui-disabled': { bgcolor: 'rgba(192,57,43,0.4)' },
                                    }}
                                >
                                    Create Account
                                </Button>
                            </Box>
                            </Box>
                        )}
                    </CardContent>

                    <Divider sx={{ borderColor: BORDER }} />

                    <CardActions sx={{ justifyContent: 'center', py: 2 }}>
                        <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                            Already have an account?{' '}
                            <Link
                                component="button"
                                onClick={() => navigate('/auth/sign_in')}
                                sx={{ color: BRAND_RED, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                Sign in
                            </Link>
                        </Typography>
                    </CardActions>
                </Card>
            </Container>
        </Box>
    );
}

export default SignUp;
